import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { Phone, Brain, AlertTriangle } from "lucide-react";
const AcceleratedTmsOtherConditions = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Can TMS Help With OCD, Anxiety, PTSD & Other Conditions? | Neuro Wellness TMS Centers of America"
        }
        description={
          "Where does TMS stand for conditions beyond depression? Learn what's FDA-cleared, what's still being researched, and what questions to ask. Coral Springs, FL."
        }
        canonical={"/accelerated-tms-other-conditions"}
        keywords={
          "accelerated TMS OCD, TMS FDA clearance anxiety, accelerated TMS PTSD, TMS bipolar depression, TMS eating disorders, off-label TMS"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "Can TMS Help With Conditions Other Than Depression?",
          description:
            "A patient-friendly guide to FDA clearance status of TMS for OCD, anxiety, bipolar depression, PTSD, and other conditions.",
          about: [
            "TMS FDA Clearance",
            "OCD",
            "Anxiety",
            "PTSD",
            "Off-Label TMS",
          ],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              Patient's Guide · Part 5
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              Can TMS Help With Other Conditions?
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              TMS is cleared for depression. What about OCD, anxiety, PTSD, or
              others? Here's where the science stands — in plain language.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            {/* The rule — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Brain className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  The basic rule
                </h2>
              </div>
              <p>
                FDA clearance is specific. It applies to a particular device,
                for a particular condition, using a particular protocol, in a
                particular group of people. A device cleared for depression
                isn't automatically cleared for OCD. A protocol cleared for one
                session per day isn't automatically cleared for ten.
              </p>
              <p>
                SAINT is cleared for major depressive disorder in adults who
                haven't responded adequately to antidepressants. That's the full
                scope of its clearance.
              </p>
            </div>

            {/* OCD — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                OCD
              </h2>
              <p>
                TMS has been FDA-cleared for OCD since 2018 — as an add-on
                treatment, meaning alongside medication and therapy. The
                standard protocol runs about 29 sessions over six weeks.
              </p>
              <p>
                Accelerated OCD protocols are still being researched. Early
                studies show promising results, but there's an honest question
                about whether the results last. Compressing the schedule may get
                you there faster — whether it holds is the open question, and
                longer courses or maintenance sessions may be needed.
              </p>
            </div>

            {/* Anxiety — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Anxiety
              </h2>
              <p>
                Read carefully. There's a clearance for depression that comes
                with anxiety — "anxious depression" — using a specific deep TMS
                approach. That's not the same as a clearance for generalized
                anxiety disorder on its own. If a clinic says TMS is "cleared
                for anxiety," ask: which clearance, for which device, and for
                which diagnosis?
              </p>
            </div>

            {/* Bipolar, PTSD, adolescents — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Bipolar depression, PTSD, and adolescents
              </h2>
              <p>
                These are active research areas. Some devices have specific
                clearances; others are still in trials. Adolescent depression
                clearances now exist for certain systems. PTSD indications are
                device-specific. Because this changes several times a year, the
                FDA database is more reliable than any article — including this
                one.
              </p>
            </div>

            {/* Eating disorders — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Eating disorders
              </h2>
              <p>
                There is no FDA-cleared TMS indication for eating disorders, and
                the research is limited. If restrictive eating is part of your
                situation, mention it during screening. Nutritional and medical
                stability affects both safety and whether TMS is appropriate for
                you.
              </p>
            </div>

            {/* What to ask — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Questions to ask if a clinic claims a condition
              </h2>
              <ul className="space-y-3">
                {[
                  "Is that an FDA-cleared indication for the device you use, or is it off-label?",
                  "What evidence supports it — studies in whom, at what size, with what follow-up?",
                  "How long did the results last?",
                  "Will insurance cover an off-label use? (Almost always: no.)",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                Off-label treatment isn't wrong — it's common in psychiatry. But
                you deserve to know that's what you're getting.
              </p>
            </div>

            {/* If you're weighing this decision — simplified */}
            <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                <div>
                  <h3 className="font-headings text-lg font-bold text-primary">
                    If you're weighing this decision
                  </h3>
                  <p className="mt-2 text-[16px] leading-relaxed text-muted-foreground">
                    Deciding among treatments after several haven't worked is
                    genuinely hard. Take your time with the verification steps,
                    and bring someone you trust into the conversation. If things
                    feel worse while you're deciding, tell your care team now
                    rather than waiting. The 988 Suicide & Crisis Lifeline (call
                    or text 988) is available any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Sources — simplified */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-headings text-lg font-bold text-primary">
                Where to verify
              </h3>
              <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-muted-foreground">
                <li>
                  Magnus Medical — SAINT provider directory: magnusmedical.com
                </li>
                <li>FDA device databases: accessdata.fda.gov</li>
                <li>
                  Cole EJ et al., American Journal of Psychiatry, 2022
                  (double-blind trial)
                </li>
                <li>
                  Cole EJ et al., American Journal of Psychiatry, 2020
                  (open-label study)
                </li>
                <li>
                  Medicare.gov for current Part B deductible and coinsurance
                </li>
                <li>Your own plan's medical policy document for TMS</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-light-blue bg-light-blue/30 p-6">
              <h3 className="font-headings text-lg font-bold text-primary">
                Read the full guide
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/find-a-verified-saint-provider"
                  className="text-accent hover:underline"
                >
                  Part 1: How to Verify a Clinic Is Credentialed for SAINT →
                </Link>
                <Link
                  to="/accelerated-tms-fmri-targeting"
                  className="text-accent hover:underline"
                >
                  Part 2: How Brain Mapping Works →
                </Link>
                <Link
                  to="/accelerated-tms-coverage"
                  className="text-accent hover:underline"
                >
                  Part 3: What Coverage Looks Like →
                </Link>
                <Link
                  to="/accelerated-tms-five-day-week"
                  className="text-accent hover:underline"
                >
                  Part 4: What the Five-Day Week Is Like →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Related Resources"
          links={[...allTreatmentsLinks, ...allDecisionLinks]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default AcceleratedTmsOtherConditions;
